/**
 * Delivery Map Component
 * Interactive map for delivery and pickup points
 */

class DeliveryMap {
  constructor() {
    this.map = null;
    this.markers = [];
    this.activeFilter = 'all';
    this.listVisible = false;
    this.googleMapsLoaded = false;
    
    this.init();
  }

  init() {
    console.log('=== DELIVERY MAP INITIALIZING ===');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupMap());
    } else {
      this.setupMap();
    }
  }

  setupMap() {
    // Get section data from Liquid template
    this.sectionData = this.getSectionData();
    
    // Setup locations and create cards immediately
    this.setupLocationsData();
    this.createLocationCards();
    
    // Show cards while map loads (only on desktop)
    const cardsElement = document.getElementById('locationCards');
    if (cardsElement && window.innerWidth > 768) {
      cardsElement.style.display = 'block';
    }

    if (!this.sectionData.googleMapsApiKey) {
      console.error('Google Maps API Key not provided');
      this.showError('API Key de Google Maps no configurada');
      return;
    }

    // Load Google Maps API on both desktop and mobile
    this.loadGoogleMapsAPI();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Handle window resize - trigger map resize if it exists
    window.addEventListener('resize', () => {
      if (this.map && window.google && window.google.maps) {
        setTimeout(() => {
          window.google.maps.event.trigger(this.map, 'resize');
        }, 100);
      }
    });
    
    // Setup locations data
    this.setupLocationsData();
  }

  getSectionData() {
    // Get configuration from Liquid template or use defaults
    return window.deliveryMapConfig || {
      googleMapsApiKey: 'AIzaSyD6OPUQJCcuFkBwQbWRnqR0zqnUAQiWsGo', // The Vault API Key (fallback)
      defaultLatitude: 19.4326,
      defaultLongitude: -99.1332,
      defaultZoom: 12,
      fullScreenMap: false
    };
  }

  loadGoogleMapsAPI() {
    if (window.google && window.google.maps) {
      this.googleMapsLoaded = true;
      this.initializeMap();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.sectionData.googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.googleMapsLoaded = true;
      this.initializeMap();
    };
    script.onerror = (error) => {
      console.error('Failed to load Google Maps API:', error);
      this.showApiError();
    };
    
    document.head.appendChild(script);
  }

  initializeMap() {
    console.log('Initializing Google Map...');
    
    const mapContainer = document.getElementById('deliveryMap');
    if (!mapContainer) {
      console.error('Map container not found');
      return;
    }

    // Hide loading indicator
    this.hideLoading();

    // Adjust center and zoom for mobile
    let center = {
      lat: parseFloat(this.sectionData.defaultLatitude),
      lng: parseFloat(this.sectionData.defaultLongitude)
    };
    let zoom = parseInt(this.sectionData.defaultZoom);

    // Calculate center point between all 4 locations (only on mobile)
    if (window.innerWidth <= 768) {
      const locations = this.locations;
      if (locations && locations.length > 0) {
        let totalLat = 0;
        let totalLng = 0;
        let minLat = locations[0].latitude;
        let maxLat = locations[0].latitude;
        let minLng = locations[0].longitude;
        let maxLng = locations[0].longitude;

        locations.forEach(location => {
          totalLat += location.latitude;
          totalLng += location.longitude;
          minLat = Math.min(minLat, location.latitude);
          maxLat = Math.max(maxLat, location.latitude);
          minLng = Math.min(minLng, location.longitude);
          maxLng = Math.max(maxLng, location.longitude);
        });

        // Calculate center point
        const centerLat = totalLat / locations.length;
        const centerLng = totalLng / locations.length;

        center = {
          lat: centerLat,
          lng: centerLng
        };

        // Adjust zoom based on spread of locations
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;
        const maxDiff = Math.max(latDiff, lngDiff);
        
        // Calculate appropriate zoom level (balanced for mobile)
        if (maxDiff > 0.05) zoom = 11;
        else if (maxDiff > 0.02) zoom = 12;
        else if (maxDiff > 0.01) zoom = 13;
        else zoom = 14;
      }
    }

    // Initialize map
    this.map = new google.maps.Map(mapContainer, {
      center: center,
      zoom: zoom,
      styles: this.getMapStyles(),
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: 'cooperative',
      scrollwheel: false,
      disableDoubleClickZoom: false
    });

    // Add markers for all locations
    this.addMarkers();
    
    // Add click listener to map
    this.map.addListener('click', () => {
      this.clearSelectedLocation();
      this.clearActiveCard();
    });

    console.log('Map initialized successfully');
  }

  getMapStyles() {
    return [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#212121"
          }
        ]
      },
      {
        "elementType": "labels.icon",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#212121"
          }
        ]
      },
      {
        "featureType": "administrative",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "featureType": "administrative.country",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#9e9e9e"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "administrative.locality",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#bdbdbd"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#181818"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#616161"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1b1b1b"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry.fill",
        "stylers": [
          {
            "color": "#2c2c2c"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#8a8a8a"
          }
        ]
      },
      {
        "featureType": "road.arterial",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#373737"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#3c3c3c"
          }
        ]
      },
      {
        "featureType": "road.highway.controlled_access",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#4e4e4e"
          }
        ]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#616161"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#000000"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#3d3d3d"
          }
        ]
      }
    ];
  }

  setupLocationsData() {
    // Define locations directly since we removed the DOM elements
    this.locations = [
      {
        name: "Hobby Mart",
        address: "Calle Milton 2, Col. Anzures, Miguel Hidalgo, CDMX",
        phone: "55 5250 7501",
        hours: "Lunes a viernes: 17:00 – 22:00, Sábados y domingos: 11:00 – 17:00",
        latitude: 19.428189,
        longitude: -99.175691,
        type: "pickup"
      },
      {
        name: "Versus Tienda",
        address: "Av. Vía Adolfo López Mateos Manzana 001, Bosques de Moctezuma, 53150 Naucalpan de Juárez, Méx.",
        phone: "+52 55 6893 7057",
        hours: "Lunes a domingo: 11:30 AM – 8:00 PM",
        latitude: 19.483551,
        longitude: -99.249604,
        type: "delivery"
      },
      {
        name: "Magia para llevar",
        address: "Eje Central Lázaro Cárdenas 511-A, Valle del Tepeyac, Gustavo A. Madero, 07730 Ciudad de México, CDMX",
        phone: "55 8765 4321",
        hours: "Lunes: Cerrado, Martes, Miércoles, Viernes, Sábado: 12:00 PM – 8:00 PM, Jueves: 12:00 PM – 10:00 PM, Domingo: 12:00 PM – 6:00 PM",
        latitude: 19.488642,
        longitude: -99.143892,
        type: "pickup"
      },
      {
        name: "Neo Rockshow",
        address: "Av. Juárez 26, Colonia Centro, Centro, Cuauhtémoc, 06000 Ciudad de México, CDMX",
        phone: "Contáctanos",
        hours: "2pm - 5pm",
        specialNote: "Solo Sábados - Buscar afuera del estacionamiento y enfrente del BTK, busca a Julio",
        latitude: 19.4341083,
        longitude: -99.1430625,
        type: "pickup"
      }
    ];

    console.log(`Loaded ${this.locations.length} locations`);
  }

  addMarkers() {
    if (!this.map) return;

    // Clear existing markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    this.locations.forEach(location => {
      const marker = new google.maps.Marker({
        position: {
          lat: location.latitude,
          lng: location.longitude
        },
        map: this.map,
        title: location.name,
        icon: this.getMarkerIcon(location.type, location.name, location.specialNote),
        animation: google.maps.Animation.DROP
      });

      // No need for info windows since we have cards on the right
      // Just highlight the corresponding card when pin is clicked
      marker.addListener('click', () => {
        const card = document.querySelector(`[data-location-index="${index}"]`);
        if (card) {
          this.setActiveCard(card);
          // Center map on location
          this.map.setCenter({
            lat: location.latitude,
            lng: location.longitude
          });
          this.map.setZoom(16);
        }
      });

      this.markers.push(marker);
    });
  }

  getMarkerIcon(type, locationName = '', specialNote = '') {
    // Calculate text width more precisely
    const textWidth = Math.max(locationName.length * 7, 50); // 7px per character, minimum 50px
    const iconSpace = 35; // Space for icon + spacing
    const padding = 20; // Total padding (10px each side)
    const totalWidth = textWidth + iconSpace + padding;
    const totalHeight = 50; // Increased height to accommodate pointer
    const pointerSize = 8; // Size of the pointer
    const iconSize = new google.maps.Size(totalWidth, totalHeight);
    const iconAnchor = new google.maps.Point(totalWidth / 2, totalHeight); // Anchor at bottom center (pointer tip)
    
    // Create chip-style marker with yellow background, transparency and pointer
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <!-- Main chip body -->
          <rect x="1" y="1" width="${totalWidth - 2}" height="${totalHeight - pointerSize - 2}" rx="19" fill="#FFD700" stroke="rgba(255,255,255,0.8)" stroke-width="1" filter="url(#shadow)" opacity="0.9"/>
          <!-- Pointer -->
          <polygon points="${totalWidth/2 - pointerSize/2},${totalHeight - pointerSize - 2} ${totalWidth/2 + pointerSize/2},${totalHeight - pointerSize - 2} ${totalWidth/2},${totalHeight - 2}" fill="#FFD700" stroke="rgba(255,255,255,0.8)" stroke-width="1" filter="url(#shadow)" opacity="0.9"/>
          <!-- Icon and text -->
          <text x="12" y="26" font-size="14" fill="#000000" font-family="Arial, sans-serif" font-weight="bold">🏛️</text>
          <text x="35" y="24" font-size="13" fill="#000000" font-family="Arial, sans-serif" font-weight="bold">${locationName}</text>
          ${specialNote ? `<text x="35" y="36" font-size="9" fill="#666666" font-family="Arial, sans-serif" font-style="italic">${specialNote}</text>` : ''}
        </svg>
      `),
      scaledSize: iconSize,
      anchor: iconAnchor
    };
  }

  createInfoWindowContent(location) {
    return `
      <div class="custom-info-window">
        <h4>${location.name}</h4>
        <p><strong>Dirección:</strong><br>${location.address}</p>
        ${location.phone ? `<p><strong>Teléfono:</strong><br>${location.phone}</p>` : ''}
        ${location.hours ? `<p><strong>Horarios:</strong><br>${location.hours}</p>` : ''}
        <div class="info-actions">
          <a href="https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}" 
             target="_blank" class="btn btn--small btn--outline">
            <svg class="icon" viewBox="0 0 24 24" width="14" height="14">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
            </svg>
            Direcciones
          </a>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    // Handle window resize
    window.addEventListener('resize', () => {
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize');
      }
    });
  }


  hideLoading() {
    const loading = document.getElementById('mapLoading');
    if (loading) {
      loading.style.display = 'none';
    }
    // Show location cards when map finishes loading
    const cardsElement = document.getElementById('locationCards');
    if (cardsElement) {
      cardsElement.style.display = 'block';
    }
  }

  showError(message) {
    const mapContainer = document.getElementById('deliveryMap');
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          height: 300px;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          color: #dc3545;
          font-size: 16px;
          text-align: center;
          padding: 20px;
        ">
          <div>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" style="margin-bottom: 10px;">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <p style="margin: 0;">${message}</p>
          </div>
        </div>
      `;
    }
  }

  showApiError() {
    const mapContainer = document.getElementById('deliveryMap');
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-align: center;
          padding: 40px;
          border-radius: 12px;
        ">
          <div style="max-width: 600px;">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" style="margin-bottom: 20px; opacity: 0.9;">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <h3 style="margin: 0 0 15px 0; font-size: 24px; font-weight: 600;">🗺️ Mapa de Puntos de Entrega</h3>
            <p style="margin: 0 0 20px 0; font-size: 16px; opacity: 0.9; line-height: 1.5;">
              Para mostrar el mapa interactivo, necesitas activar la API de Google Maps en Google Cloud Console.
            </p>
            <div style="
              background: rgba(255, 255, 255, 0.1);
              border-radius: 8px;
              padding: 20px;
              margin: 20px 0;
              backdrop-filter: blur(10px);
            ">
              <h4 style="margin: 0 0 10px 0; font-size: 18px;">📋 Pasos para activar:</h4>
              <ol style="text-align: left; margin: 0; padding-left: 20px; opacity: 0.9;">
                <li>Ve a <a href="https://console.cloud.google.com/" target="_blank" style="color: #ffd700; text-decoration: underline;">Google Cloud Console</a></li>
                <li>Habilita "Maps JavaScript API"</li>
                <li>Configura las restricciones de tu API Key</li>
                <li>Recarga esta página</li>
              </ol>
            </div>
            <div style="margin-top: 30px;">
              <h4 style="margin: 0 0 15px 0; font-size: 18px;">📍 Nuestras Ubicaciones:</h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 15px; text-align: left;">
                <div style="background: rgba(255, 255, 255, 0.1); padding: 15px; border-radius: 8px;">
                  <h5 style="margin: 0 0 8px 0; color: #ffd700;">🟢 The Vault - Tienda Principal</h5>
                  <p style="margin: 0; font-size: 14px; opacity: 0.8;">Av. Insurgentes Sur 123, Roma Norte</p>
                  <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">📞 +52 55 1234 5678</p>
                </div>
                <div style="background: rgba(255, 255, 255, 0.1); padding: 15px; border-radius: 8px;">
                  <h5 style="margin: 0 0 8px 0; color: #ffd700;">🟡 The Vault - Polanco</h5>
                  <p style="margin: 0; font-size: 14px; opacity: 0.8;">Av. Masaryk 456, Polanco</p>
                  <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">📞 +52 55 8765 4321</p>
                </div>
                <div style="background: rgba(255, 255, 255, 0.1); padding: 15px; border-radius: 8px;">
                  <h5 style="margin: 0 0 8px 0; color: #ffd700;">🟢 The Vault - Centro Histórico</h5>
                  <p style="margin: 0; font-size: 14px; opacity: 0.8;">Plaza de la Constitución 789</p>
                  <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">📞 +52 55 9876 5432</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
  }

  // Public methods for external access
  getMap() {
    return this.map;
  }

  getLocations() {
    return this.locations;
  }

  addLocation(locationData) {
    // Method to dynamically add new locations
    this.locations.push(locationData);
    this.addMarkers();
  }

  removeLocation(locationId) {
    // Method to remove locations
    this.locations = this.locations.filter(loc => loc.id !== locationId);
    this.addMarkers();
  }

  createLocationCards() {
    const cardsContainer = document.getElementById('cardsList');
    if (!cardsContainer) return;

    cardsContainer.innerHTML = '';

    this.locations.forEach((location, index) => {
      const card = document.createElement('div');
      card.className = 'location-card';
      card.dataset.locationIndex = index;
      
      card.innerHTML = `
        <div class="location-types">
          <span class="location-type pickup">Recolección</span>
          <span class="location-type delivery">Entrega</span>
        </div>
        <h4>${location.name}</h4>
        <p class="location-address">📍 ${location.address}</p>
        ${location.phone ? `<p class="location-phone">📞 ${location.phone}</p>` : ''}
        ${location.hours ? `<p class="location-hours">🕒 ${location.hours}</p>` : ''}
        ${location.specialNote ? `<p class="location-special">⚠️ ${location.specialNote}</p>` : ''}
      `;

      // Add click event to card
      card.addEventListener('click', () => {
        this.selectLocation(index);
        this.setActiveCard(card);
      });

      cardsContainer.appendChild(card);
    });
  }

  setActiveCard(activeCard) {
    // Remove active class from all cards
    document.querySelectorAll('.location-card').forEach(card => {
      card.classList.remove('active');
    });
    
    // Add active class to clicked card
    activeCard.classList.add('active');
  }

  clearActiveCard() {
    document.querySelectorAll('.location-card').forEach(card => {
      card.classList.remove('active');
    });
  }

  selectLocation(locationIndex) {
    const location = this.locations[locationIndex];
    if (!location) return;

    // Center map on location
    this.map.setCenter({
      lat: location.latitude,
      lng: location.longitude
    });
    
    // Adjust zoom based on screen size
    const zoom = window.innerWidth <= 768 ? 14 : 16;
    this.map.setZoom(zoom);

    // Close any existing info window
    if (this.infoWindow) {
      this.infoWindow.close();
      this.infoWindow = null;
    }
  }

  clearSelectedLocation() {
    if (this.infoWindow) {
      this.infoWindow.close();
      this.infoWindow = null;
    }
  }
}

// Initialize when script loads
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('deliveryMap')) {
    window.deliveryMap = new DeliveryMap();
    console.log('Delivery Map initialized');
  }
});

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DeliveryMap;
}
